import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, filter, switchMap } from 'rxjs/operators';
import { MFA_COMPLETED, SUCCESSFUL_ACCOUNT_UNLOCK } from '../../../constants';
import {
  ContinueAccountRecovery,
  ContinueAuthentication,
  InitiateAccountRecovery,
  InitiateAuthentication,
  InitiateSsoSession,
  OperationTypes,
  SelectDevice,
  SubmitEmailOtp,
  SubmitNewPassword,
  SubmitSkymilesNumber
} from '../../../models';
import { LoginOAuthParams } from '../../../models/oauth.interfaces';
import { SelfServicePasswordResetHelperService } from './self-service-password-reset-helper.service';
@Injectable({
  providedIn: 'root'
})
export class SelfServicePasswordResetWrapperService extends SelfServicePasswordResetHelperService {
  selfServiceResetPasswordFlow(
    loginOAuthParams: LoginOAuthParams,
    operationType?: OperationTypes,
    skymilesNumber?: string,
    deviceRef?: string,
    otp?: string,
    newPassword?: string,
    flowId?: string
  ): Observable<
    | InitiateAccountRecovery
    | SelectDevice
    | SubmitSkymilesNumber
    | SubmitEmailOtp
    | SubmitNewPassword
    | InitiateAuthentication
    | InitiateSsoSession
    | ContinueAuthentication
    | ContinueAccountRecovery
    | string
  > {
    let fn;

    const options = {
      INITIATE_RESET_PASSWORD: () => {
        return this.initializeSelfServiceReset(loginOAuthParams);
      },
      SUBMIT_SKYMILES_NUMBER: () => {
        const skyMilesNumber = skymilesNumber ?? '';
        return this.submitSkymilesNumber(loginOAuthParams, skyMilesNumber);
      },
      INITIATE_DEVICE_METHOD: () => {
        const id = flowId ?? this.flowId;
        return this.authenticate(loginOAuthParams, id);
      },
      SELECT_DEVICE_METHOD: () => {
        const deviceReferenceId = deviceRef ?? '';
        return this.selectDeviceMethod(loginOAuthParams, deviceReferenceId);
      },
      SUBMIT_OTP: () => {
        const oneTimePassCode = otp ?? '';
        return this.submitOtp(loginOAuthParams, oneTimePassCode);
      },
      SUBMIT_NEW_PASSWORD: () => {
        const password = newPassword ?? '';
        return this.submitNewPasswordRequest(loginOAuthParams, password);
      },
      INITIATE_FLY_DELTA_POLL: () => {
        return this.initiateFlyDeltaPoll(loginOAuthParams);
      },
      default: () => {
        return this.initializeSelfServiceReset(loginOAuthParams);
      }
    };
    operationType ? (fn = options[operationType]) : (fn = options['default']);
    return fn();
  }

  submitOtp(
    loginOAuthParams: LoginOAuthParams,
    otp: string
  ): Observable<ContinueAuthentication | string> {
    return this.submitEmailOtp
      .submitEmailOtp(loginOAuthParams.oauthConfig, this.flowId, otp)
      .pipe(
        filter((response) => response.status === MFA_COMPLETED),
        concatMap((response: SubmitEmailOtp) => {
          return this.continueAuthentication
            .continueAuthentication(
              loginOAuthParams.oauthConfig,
              response.id ? response.id : ''
            )
            .pipe(
              switchMap((data) => {
                if (data.status === SUCCESSFUL_ACCOUNT_UNLOCK) {
                  return this.continueAccountRecovery.continueAccountRecovery(
                    loginOAuthParams.oauthConfig,
                    data.id ? data.id : ''
                  );
                } else {
                  return of(data);
                }
              })
            );
        }),
        catchError((error) => {
          return of(this.mfaUtils.getMfaRegistrationError(error));
        })
      );
  }

  submitNewPasswordRequest(
    loginOAuthParams: LoginOAuthParams,
    newPassword: string
  ): Observable<SubmitNewPassword | string> {
    return this.submitNewPassword
      .submitNewPassword(loginOAuthParams.oauthConfig, this.flowId, newPassword)
      .pipe(
        catchError((error) => {
          return of(this.mfaUtils.checkResetPasswordError(error));
        })
      );
  }
}
